import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.agrixagro.com';
const DEFAULT_TITLE = 'Agrix Agrotech | Farming-as-a-Service for Smallholder Farmers';
const DEFAULT_DESCRIPTION =
  'Agrix Agrotech helps small and marginal farmers with farm mechanisation, quality agri-inputs, digital farm management, and market linkages.';

const pageMetadata = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  '/about': {
    title: 'About Agrix Agrotech | Building Profitable Farming Clusters',
    description:
      'Learn how Agrix Agrotech supports small and marginal farmers through technology-enabled farming clusters and end-to-end services.',
  },
  '/services': {
    title: 'Farm Machinery Rental, Agri Inputs & Market Linkage | Agrix',
    description:
      'Explore Agrix services: machinery rental, quality agri-inputs, digital farm management, expert support, and market linkage for farmers.',
  },
  '/tech': {
    title: 'Digital Farm Management & Plot Mapping | Agrix Agrotech',
    description:
      'See how Agrix Agrotech uses digital plot mapping, data, and farm technology to help farming clusters make better decisions.',
  },
  '/resources': {
    title: 'Agriculture Resources | Agrix Agrotech',
    description:
      'Resources and insights from Agrix Agrotech for farmers, farming communities, and agricultural partners.',
  },
  '/newsroom': {
    title: 'Agrix Newsroom | Farming Innovation & Company Updates',
    description:
      'Read the latest Agrix Agrotech announcements, agricultural innovation updates, media coverage, and videos.',
  },
  '/contact': {
    title: 'Contact Agrix Agrotech | Patna & Delhi Offices',
    description:
      'Contact Agrix Agrotech to learn about farming services, partnerships, and operations in Patna, Bihar and New Delhi.',
  },
  '/privacy': {
    title: 'Privacy Policy | Agrix Agrotech',
    description: 'Read the Agrix Agrotech privacy policy.',
  },
  '/cookies': {
    title: 'Cookies Policy | Agrix Agrotech',
    description: 'Read the Agrix Agrotech cookies policy.',
  },
  '/disclaimer': {
    title: 'Disclaimer | Agrix Agrotech',
    description: 'Read the Agrix Agrotech website disclaimer.',
  },
};

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const [attributeName, attributeValue] = attribute.split('=');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
};

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const metadata = pageMetadata[pathname] ?? {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    };
    const canonicalUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    document.title = metadata.title;
    setMeta('meta[name="description"]', 'name=description', metadata.description);
    setMeta('meta[property="og:title"]', 'property=og:title', metadata.title);
    setMeta('meta[property="og:description"]', 'property=og:description', metadata.description);
    setMeta('meta[property="og:url"]', 'property=og:url', canonicalUrl);
    setMeta('meta[name="twitter:title"]', 'name=twitter:title', metadata.title);
    setMeta('meta[name="twitter:description"]', 'name=twitter:description', metadata.description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let schema = document.getElementById('organization-schema');
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'organization-schema';
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Agrix Agrotech Private Limited',
      alternateName: 'Agrix',
      url: SITE_URL,
      email: 'operations@agrixagro.com',
      telephone: '+91-9031018890',
      sameAs: [
        'https://www.linkedin.com/company/agrixagrotech',
        'https://www.facebook.com/AgrixIndia/',
        'https://www.instagram.com/agrixindia/',
        'https://www.youtube.com/@agrixindia',
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'L1/24, First Floor, Sri Krishna Puri, Boring Road',
        addressLocality: 'Patna',
        addressRegion: 'Bihar',
        postalCode: '800001',
        addressCountry: 'IN',
      },
    });
  }, [pathname]);

  return null;
}
